import { forwardRef } from "react";

import { Box } from "@mui/material";

import type { AssistantFeature } from "../../contexts/AssistantConfigContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { Communications } from "@/icons/special-themes";

interface LauncherProps {
  feature: AssistantFeature;
  isPanelOpen: boolean;
  isHidden?: boolean;
  onClick: () => void;
}

export const Launcher = forwardRef<HTMLButtonElement, LauncherProps>(
  ({ feature, isPanelOpen, isHidden = false, onClick }, ref) => {
    const { t } = useTranslate();
    if (isHidden) {
      return null;
    }
    return (
      <DashboardButton
        ref={ref}
        vibe="brand"
        variant="filled"
        aria-label={t("dashboard", "assistant.widget.launcher")}
        aria-expanded={isPanelOpen}
        data-test={`${feature}-launcher`}
        onClick={onClick}
        sx={(theme) => ({
          position: "fixed",
          bottom: theme.spacing(2),
          right: theme.spacing(2),
          zIndex: theme.zIndex.hubspot - 1,
          display: { xs: isPanelOpen ? "none" : "inline-flex", md: "inline-flex" },
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
          width: theme.spacing(8),
          height: theme.spacing(8),
          padding: 0,
          borderRadius: "50%",
        })}
      >
        <Box
          component={Communications}
          aria-hidden
          sx={(theme) => ({
            width: theme.spacing(3),
            height: theme.spacing(3),
            fill: theme.palette.text.reverse.intense,
          })}
        />
      </DashboardButton>
    );
  }
);

Launcher.displayName = "Launcher";
